/**
 * API Service Layer
 * Handles all communication with the Cloudflare Worker backend
 */

const API_BASE = import.meta.env.PROD ? '/api' : '/api';

class ApiError extends Error {
  constructor(message, status, options = {}) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
    this.isOffline = options.isOffline || false;
    this.isAuthExpired = options.isAuthExpired || false;
    this.isRateLimited = options.isRateLimited || false;
    this.retryAfter = options.retryAfter || null; // seconds until retry
  }
}

// Rate limit state (module-level for simplicity)
let rateLimitState = {
  isLimited: false,
  retryAfter: null,
  resetTime: null,
  listeners: new Set(),
};

/**
 * Subscribe to rate limit state changes
 */
export function subscribeToRateLimit(callback) {
  rateLimitState.listeners.add(callback);
  // Immediately call with current state
  callback(rateLimitState);
  return () => rateLimitState.listeners.delete(callback);
}

/**
 * Notify all rate limit listeners
 */
function notifyRateLimitListeners() {
  rateLimitState.listeners.forEach(cb => cb(rateLimitState));
}

/**
 * Set rate limit state
 */
function setRateLimited(retryAfter) {
  rateLimitState.isLimited = true;
  rateLimitState.retryAfter = retryAfter;
  rateLimitState.resetTime = Date.now() + (retryAfter * 1000);
  notifyRateLimitListeners();
  
  // Auto-clear after retry period
  setTimeout(() => {
    rateLimitState.isLimited = false;
    rateLimitState.retryAfter = null;
    rateLimitState.resetTime = null;
    notifyRateLimitListeners();
  }, retryAfter * 1000);
}

/**
 * Get current rate limit state
 */
export function getRateLimitState() {
  return { ...rateLimitState };
}

async function request(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;
  
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };
  
  // For local development, add dev user header
  if (import.meta.env.DEV) {
    config.headers['X-Dev-User-Email'] = 'dev@example.com';
  }
  
  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      // Check for auth expiry (401 Unauthorized or 403 Forbidden)
      if (response.status === 401 || response.status === 403) {
        throw new ApiError('Session expired. Please log in again.', response.status, { isAuthExpired: true });
      }
      
      // Check for rate limiting (429 Too Many Requests)
      if (response.status === 429) {
        const retryAfter = parseInt(response.headers.get('Retry-After')) || 60;
        setRateLimited(retryAfter);
        throw new ApiError(
          `Too many requests. Please wait ${retryAfter} seconds.`, 
          response.status, 
          { isRateLimited: true, retryAfter }
        );
      }
      
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      
      // Check if this is an offline response from service worker
      if (error.offline) {
        throw new ApiError(error.message || 'Offline', response.status, { isOffline: true });
      }
      
      throw new ApiError(error.error || 'Request failed', response.status);
    }
    
    return response.json();
  } catch (err) {
    // Re-throw ApiErrors as-is
    if (err instanceof ApiError) {
      throw err;
    }
    
    // Network error (truly offline, no service worker cache)
    if (err.name === 'TypeError' && err.message.includes('fetch')) {
      throw new ApiError('Network unavailable', 0, { isOffline: true });
    }
    throw err;
  }
}

// User API
export const userApi = {
  getMe: () => request('/user'),
  
  updatePreferences: (preferences) => request('/user/preferences', {
    method: 'PUT',
    body: JSON.stringify(preferences),
  }),
};

// Events API
export const eventsApi = {
  list: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.start) searchParams.set('start', params.start);
    if (params.end) searchParams.set('end', params.end);
    if (params.category) searchParams.set('category', params.category);
    
    const query = searchParams.toString();
    return request(`/events${query ? `?${query}` : ''}`);
  },
  
  get: (id) => request(`/events/${id}`),
  
  create: (event) => request('/events', {
    method: 'POST',
    body: JSON.stringify(event),
  }),
  
  update: (id, event) => request(`/events/${id}`, {
    method: 'PUT',
    body: JSON.stringify(event),
  }),
  
  delete: (id) => request(`/events/${id}`, {
    method: 'DELETE',
  }),
};

// Expenses API
export const expensesApi = {
  list: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.category) searchParams.set('category', params.category);
    if (params.is_planned !== undefined) searchParams.set('is_planned', params.is_planned);
    if (params.start) searchParams.set('start', params.start);
    if (params.end) searchParams.set('end', params.end);
    if (params.event_id) searchParams.set('event_id', params.event_id);
    if (params.year) searchParams.set('year', params.year);
    if (params.trip_id) searchParams.set('trip_id', params.trip_id);
    
    const query = searchParams.toString();
    return request(`/expenses${query ? `?${query}` : ''}`);
  },
  
  get: (id) => request(`/expenses/${id}`),
  
  create: (expense) => request('/expenses', {
    method: 'POST',
    body: JSON.stringify(expense),
  }),
  
  update: (id, expense) => request(`/expenses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(expense),
  }),
  
  delete: (id) => request(`/expenses/${id}`, {
    method: 'DELETE',
  }),
};

// Budgets API
export const budgetsApi = {
  list: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.year) searchParams.set('year', params.year);
    if (params.trip_id) searchParams.set('trip_id', params.trip_id);
    const query = searchParams.toString();
    return request(`/budgets${query ? `?${query}` : ''}`);
  },
  
  getSummary: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.year) searchParams.set('year', params.year);
    if (params.trip_id) searchParams.set('trip_id', params.trip_id);
    const query = searchParams.toString();
    return request(`/budgets/summary${query ? `?${query}` : ''}`);
  },
  
  getYears: () => request('/budgets/years'),
  
  set: (budget) => request('/budgets', {
    method: 'POST',
    body: JSON.stringify(budget),
  }),
  
  delete: (id) => request(`/budgets/${id}`, {
    method: 'DELETE',
  }),
};

// Notifications API
export const notificationsApi = {
  list: (pendingOnly = false) => {
    const query = pendingOnly ? '?pending=true' : '';
    return request(`/notifications${query}`);
  },
  
  create: (notification) => request('/notifications', {
    method: 'POST',
    body: JSON.stringify(notification),
  }),
  
  delete: (id) => request(`/notifications/${id}`, {
    method: 'DELETE',
  }),
};

// Attachments API
export const attachmentsApi = {
  // List attachments for an event or expense
  list: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.event_id) searchParams.set('event_id', params.event_id);
    if (params.expense_id) searchParams.set('expense_id', params.expense_id);
    
    const query = searchParams.toString();
    return request(`/attachments${query ? `?${query}` : ''}`);
  },
  
  // Get attachment metadata
  getMetadata: (id) => request(`/attachments/${id}/metadata`),
  
  // Upload a new attachment (uses FormData instead of JSON)
  upload: async (file, { event_id, expense_id }) => {
    const formData = new FormData();
    formData.append('file', file);
    if (event_id) formData.append('event_id', event_id);
    if (expense_id) formData.append('expense_id', expense_id);
    
    const url = `${API_BASE}/attachments`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        body: formData,
        // Don't set Content-Type header - browser will set it with boundary
      });
      
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new ApiError('Session expired. Please log in again.', response.status, { isAuthExpired: true });
        }
        
        if (response.status === 429) {
          const retryAfter = parseInt(response.headers.get('Retry-After')) || 60;
          setRateLimited(retryAfter);
          throw new ApiError(
            `Too many requests. Please wait ${retryAfter} seconds.`, 
            response.status, 
            { isRateLimited: true, retryAfter }
          );
        }
        
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new ApiError(error.error || 'Upload failed', response.status);
      }
      
      return response.json();
    } catch (err) {
      if (err instanceof ApiError) throw err;
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        throw new ApiError('Network unavailable', 0, { isOffline: true });
      }
      throw err;
    }
  },
  
  // Download attachment (returns blob URL)
  download: async (id) => {
    const url = `${API_BASE}/attachments/${id}`;
    
    try {
      const response = await fetch(url);
      
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new ApiError('Session expired. Please log in again.', response.status, { isAuthExpired: true });
        }
        
        if (response.status === 429) {
          const retryAfter = parseInt(response.headers.get('Retry-After')) || 60;
          setRateLimited(retryAfter);
          throw new ApiError(
            `Too many requests. Please wait ${retryAfter} seconds.`, 
            response.status, 
            { isRateLimited: true, retryAfter }
          );
        }
        
        throw new ApiError('Download failed', response.status);
      }
      
      const blob = await response.blob();
      return {
        blob,
        url: URL.createObjectURL(blob),
        contentType: response.headers.get('Content-Type'),
      };
    } catch (err) {
      if (err instanceof ApiError) throw err;
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        throw new ApiError('Network unavailable', 0, { isOffline: true });
      }
      throw err;
    }
  },
  
  // Delete attachment
  delete: (id) => request(`/attachments/${id}`, {
    method: 'DELETE',
  }),
};

// Trips API
export const tripsApi = {
  list: () => request('/trips'),
  
  get: (id) => request(`/trips/${id}`),
  
  create: (trip) => request('/trips', {
    method: 'POST',
    body: JSON.stringify(trip),
  }),
  
  update: (id, trip) => request(`/trips/${id}`, {
    method: 'PUT',
    body: JSON.stringify(trip),
  }),
  
  delete: (id) => request(`/trips/${id}`, {
    method: 'DELETE',
  }),
};

export { ApiError };
